import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';

interface FileItem {
  mainCategory: string;
  subCategory: string;
}

interface Building {
  yearBuilt?: number;
  files: FileItem[];
}

interface Plot {
  buildings: Building[];
  files: FileItem[];
}

interface Project {
  id: string;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  usageType: string;
  usageSubType: string;
  owner: string;
  plots: Plot[];
}

// ─── Status logic ─────────────────────────────────────────────────────────────

const getProjectStatus = (project: Project): 'green' | 'blue' | 'gray' => {
  const allFiles: FileItem[] = [];
  project.plots?.forEach(plot => {
    plot.files?.forEach(f => allFiles.push(f));
    plot.buildings?.forEach(b => b.files?.forEach(f => allFiles.push(f)));
  });
  if (allFiles.length === 0) return 'gray';
  const hasIFC = allFiles.some(f => f.mainCategory === 'MODEL_3D' && f.subCategory === 'BIM');
  if (hasIFC) return 'green';
  return 'blue';
};

const STATUS_COLOR = { green: '#00c49a', blue: '#3498db', gray: '#aaaaaa' };
const STATUS_LABEL = { green: 'Bestandsmodell vorhanden', blue: 'Teilweise Daten', gray: 'Keine Daten' };

// ─── Widget config ────────────────────────────────────────────────────────────

const ALL_WIDGETS = [
  { id: 'datastand', label: 'Datenbestand' },
  { id: 'nutzungsart', label: 'Nutzungsart' },
  { id: 'baujahr', label: 'Baujahr' },
  { id: 'dateien', label: 'Dateien nach Kategorie' },
  { id: 'zuletzt', label: 'Zuletzt hinzugefügt' },
];

const DEFAULT_WIDGETS = ['datastand', 'nutzungsart', 'baujahr', 'zuletzt'];

// ─── Chart helpers ────────────────────────────────────────────────────────────

const PIE_COLORS = [
  '#00c49a', '#3498db', '#e74c3c', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#2980b9', '#c0392b', '#8e44ad',
];

const PIE_COLORS_FIXED = ['#00c49a', '#3498db', '#aaaaaa']

const PieChart: React.FC<{ data: { label: string; value: number; color?: string }[]; size?: number }> = ({ data, size = 120 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <p style={{ color: '#aaa', fontSize: '13px' }}>Keine Daten</p>;

  let currentAngle = -Math.PI / 2;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;

  const slices = data.filter(d => d.value > 0).map((d, i) => {
    const angle = (d.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(currentAngle);
    const y1 = cy + r * Math.sin(currentAngle);
    currentAngle += angle;
    const x2 = cx + r * Math.cos(currentAngle);
    const y2 = cy + r * Math.sin(currentAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    return { path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`, color: d.color || PIE_COLORS[i % PIE_COLORS.length], label: d.label, value: d.value };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((slice, i) => (
        <path key={i} d={slice.path} fill={slice.color} stroke="white" strokeWidth="1.5" />
      ))}
    </svg>
  );
};

const BarChart: React.FC<{ data: { label: string; value: number; color?: string }[] }> = ({ data }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '80px', width: '100%' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '10px', color: '#666', fontWeight: '600' }}>{d.value}</span>
          <div style={{
            width: '100%',
            height: `${Math.max((d.value / max) * 60, d.value > 0 ? 4 : 0)}px`,
            backgroundColor: d.color || '#00c49a',
            borderRadius: '3px 3px 0 0',
            transition: 'height 0.3s ease'
          }} />
          <span style={{ fontSize: '9px', color: '#888', textAlign: 'center', lineHeight: '1.2' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);

  const [projects, setProjects] = useState<Project[]>([]);
  const [view, setView] = useState<'map' | 'list'>('map');
  const [search, setSearch] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [addressResults, setAddressResults] = useState<any[]>([]);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('dashboard_widgets');
      return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
    } catch { return DEFAULT_WIDGETS; }
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  // ─── Fetch projects ─────────────────────────────────────────────────────

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjects();
  }, [token]);

  // ─── Set map location ────────────────────────────────────────────────────

  useEffect(() => {
    if (!map.current || !user.customerId) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${user.customerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const c = res.data;
      if (c.mapLat && c.mapLng && map.current) {
        map.current.flyTo({ center: [c.mapLng, c.mapLat], zoom: c.mapZoom || 13, duration: 0 });
      }
    }).catch(console.error);
  }, [map.current]); // eslint-disable-line

  // ─── Init map ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      center: [6.7780319, 51.4360495],
      zoom: 12
    });
    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
  }, []);

  // ─── Add markers ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!map.current || projects.length === 0) return;
    const addMarkers = () => {
      markers.current.forEach(m => m.remove());
      markers.current = [];
      projects.forEach(project => {
        if (!project.latitude || !project.longitude) return;
        const status = getProjectStatus(project);
        const color = STATUS_COLOR[status];
        const totalFiles = project.plots?.reduce((sum, p) =>
          sum + (p.files?.length || 0) + p.buildings?.reduce((s, b) => s + (b.files?.length || 0), 0), 0) || 0;
        const totalBuildings = project.plots?.reduce((sum, p) => sum + (p.buildings?.length || 0), 0) || 0;
        const popup = new maplibregl.Popup({ offset: 25, maxWidth: '280px' }).setHTML(`
          <div style="padding: 12px; font-family: sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background: ${color}; flex-shrink: 0;"></div>
              <strong style="color: #0f2744; font-size: 14px;">${project.name}</strong>
            </div>
            ${project.usageType ? `<p style="margin: 0 0 4px 0; color: #3498db; font-size: 11px; font-weight: 600;">${project.usageType}${project.usageSubType ? ' · ' + project.usageSubType : ''}</p>` : ''}
            <p style="margin: 0 0 4px 0; color: #666; font-size: 12px;">📍 ${project.street || ''}, ${project.zipCode || ''} ${project.city || ''}</p>
            ${project.owner ? `<p style="margin: 0 0 8px 0; color: #888; font-size: 12px;">🏛️ ${project.owner}</p>` : ''}
            <div style="display: flex; gap: 12px; margin-bottom: 10px;">
              <span style="font-size: 12px; color: #666;">🏢 ${totalBuildings} Gebäude</span>
              <span style="font-size: 12px; color: #666;">📁 ${totalFiles} Dateien</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 10px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background: ${color};"></div>
              <span style="font-size: 11px; color: ${color}; font-weight: 600;">${STATUS_LABEL[status]}</span>
            </div>
            <a href="/projects/${project.id}" style="display: block; text-align: center; background: #00c49a; color: white; padding: 6px 12px; border-radius: 6px; text-decoration: none; font-size: 13px; font-weight: 600;">
              Liegenschaft öffnen →
            </a>
          </div>
        `);
        const marker = new maplibregl.Marker({ color })
          .setLngLat([project.longitude, project.latitude])
          .setPopup(popup)
          .addTo(map.current!);
        markers.current.push(marker);
      });
    };
    if (map.current.loaded()) addMarkers();
    else map.current.on('load', addMarkers);
  }, [projects]);

  // ─── Address search ──────────────────────────────────────────────────────

  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 3) { setAddressResults([]); return; }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=de`,
        { headers: { 'Accept-Language': 'de' } }
      );
      setAddressResults(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => searchAddress(addressSearch), 400);
    return () => clearTimeout(timeout);
  }, [addressSearch, searchAddress]);

  const flyToAddress = (result: any) => {
    if (!map.current) return;
    map.current.flyTo({ center: [parseFloat(result.lon), parseFloat(result.lat)], zoom: 15, duration: 1200 });
    setAddressSearch(result.display_name.split(',').slice(0, 2).join(','));
    setAddressResults([]);
  };

  // ─── Statistics ──────────────────────────────────────────────────────────

 const allBuildings = projects.flatMap(p => (Array.isArray(p.plots) ? p.plots : []).flatMap(pl => Array.isArray(pl.buildings) ? pl.buildings : []));
  const totalBuildings = allBuildings.length;
  const withIFC = projects.filter(p => getProjectStatus(p) === 'green').length;
  const withData = projects.filter(p => getProjectStatus(p) !== 'gray').length;
  const noData = projects.filter(p => getProjectStatus(p) === 'gray').length;

  // Nutzungsart
  const usageMap: Record<string, number> = {};
  projects.forEach(p => {
    const key = p.usageType || 'Unbekannt';
    usageMap[key] = (usageMap[key] || 0) + 1;
  });
  const usageData = Object.entries(usageMap).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));

  // Baujahr
  const baujahrGroups = [
    { label: 'Vor 1945', color: '#e74c3c', value: 0 },
    { label: '1945–1980', color: '#e67e22', value: 0 },
    { label: '1980–2000', color: '#f39c12', value: 0 },
    { label: 'Ab 2000', color: '#00c49a', value: 0 },
    { label: 'Unbekannt', color: '#aaa', value: 0 },
  ];
  allBuildings.forEach(b => {
    const y = b.yearBuilt;
    if (!y) baujahrGroups[4].value++;
    else if (y < 1945) baujahrGroups[0].value++;
    else if (y < 1980) baujahrGroups[1].value++;
    else if (y < 2000) baujahrGroups[2].value++;
    else baujahrGroups[3].value++;
  });

  // Dateien nach Kategorie
  const allFiles = projects.flatMap(p =>
    (Array.isArray(p.plots) ? p.plots : []).flatMap(pl => [
      ...(Array.isArray(pl.files) ? pl.files : []),
      ...(Array.isArray(pl.buildings) ? pl.buildings : []).flatMap(b => Array.isArray(b.files) ? b.files : [])
    ])
  );
  const catMap: Record<string, number> = {};
  allFiles.forEach(f => { catMap[f.mainCategory] = (catMap[f.mainCategory] || 0) + 1; });
  const CAT_LABEL: Record<string, string> = { POINTCLOUD: 'Punktwolken', MODEL_3D: '3D-Modelle', DRAWING_2D: '2D-Pläne', DOCUMENT: 'Dokumente', OTHER: 'Sonstiges' };
  const catData = Object.entries(catMap).map(([k, v]) => ({ label: CAT_LABEL[k] || k, value: v }));

  // Zuletzt hinzugefügt
  const recentProjects = [...projects]
    .sort((a, b) => 0)
    .slice(0, 5);

  // ─── Widget config ────────────────────────────────────────────────────────

  const toggleWidget = (id: string) => {
    const updated = activeWidgets.includes(id)
      ? activeWidgets.filter(w => w !== id)
      : [...activeWidgets, id];
    setActiveWidgets(updated);
    localStorage.setItem('dashboard_widgets', JSON.stringify(updated));
  };

  // ─── Filtered list ────────────────────────────────────────────────────────

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.city?.toLowerCase().includes(q) ||
      p.street?.toLowerCase().includes(q) || p.owner?.toLowerCase().includes(q);
  });

  return (
    <div style={s.container}>

      {/* Sidebar */}
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a style={s.navItemActive}>Startseite</a>
          <a href="/projects" style={s.navItem}>Liegenschaften</a>
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN'].includes(user.role) && (
            <a href="/users" style={s.navItem}>Benutzer</a>
          )}
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role) && (
            <a href="/customers" style={s.navItem}>Kunden</a>
          )}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>
            Abmelden
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={s.main}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.welcome}>Hallo, {user.firstName}!</h1>
            <p style={s.subtitle}>{projects.length} Liegenschaften · {totalBuildings} Gebäude</p>
          </div>
          <button style={s.configBtn} onClick={() => setShowWidgetConfig(!showWidgetConfig)} title="Widgets konfigurieren">
            ⚙️ Widgets
          </button>
        </div>

        {/* Widget config panel */}
        {showWidgetConfig && (
          <div style={s.widgetConfigPanel}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '13px', color: '#0f2744' }}>Sichtbare Widgets</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {ALL_WIDGETS.map(w => (
                <button
                  key={w.id}
                  onClick={() => toggleWidget(w.id)}
                  style={{
                    padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                    borderColor: activeWidgets.includes(w.id) ? '#00c49a' : '#ddd',
                    backgroundColor: activeWidgets.includes(w.id) ? '#f0faf7' : 'white',
                    color: activeWidgets.includes(w.id) ? '#00c49a' : '#888',
                    cursor: 'pointer', fontSize: '12px', fontWeight: '500'
                  }}
                >
                  {activeWidgets.includes(w.id) ? '✓ ' : ''}{w.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats widgets */}
        <div style={s.widgetRow}>

          {/* Datenbestand */}
          {activeWidgets.includes('datastand') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Datenbestand</p>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <PieChart data={[
                  { label: 'Bestandsmodell', value: withIFC, color: '#00c49a' },
                  { label: 'Teilweise Daten', value: withData - withIFC, color: '#3498db' },
                  { label: 'Keine Daten', value: noData, color: '#aaaaaa' },
                ]} size={100} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { label: 'Bestandsmodell', value: withIFC, color: '#00c49a' },
                    { label: 'Teilweise Daten', value: withData - withIFC, color: '#3498db' },
                    { label: 'Keine Daten', value: noData, color: '#aaa' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', color: '#666' }}>{item.label}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#333', marginLeft: 'auto' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Nutzungsart */}
          {activeWidgets.includes('nutzungsart') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Nutzungsart</p>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <PieChart data={usageData} size={100} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
                  {usageData.slice(0, 5).map((d, i) => (
                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#333', marginLeft: 'auto' }}>{d.value}</span>
                    </div>
                  ))}
                  {usageData.length > 5 && <span style={{ fontSize: '10px', color: '#aaa' }}>+{usageData.length - 5} weitere</span>}
                </div>
              </div>
            </div>
          )}

          {/* Baujahr */}
          {activeWidgets.includes('baujahr') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Baujahr Gebäude</p>
              {totalBuildings > 0 ? (
                <BarChart data={baujahrGroups} />
              ) : (
                <p style={{ color: '#aaa', fontSize: '13px' }}>Keine Gebäudedaten</p>
              )}
            </div>
          )}

          {/* Dateien nach Kategorie */}
          {activeWidgets.includes('dateien') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Dateien nach Kategorie</p>
              {catData.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {catData.map((d, i) => (
                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: '#666', width: '80px', flexShrink: 0 }}>{d.label}</span>
                      <div style={{ flex: 1, backgroundColor: '#f0f0f0', borderRadius: '4px', height: '8px' }}>
                        <div style={{
                          width: `${(d.value / Math.max(...catData.map(c => c.value))) * 100}%`,
                          backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                          borderRadius: '4px', height: '8px'
                        }} />
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#333', width: '24px', textAlign: 'right' }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#aaa', fontSize: '13px' }}>Noch keine Dateien</p>
              )}
            </div>
          )}

          {/* Zuletzt hinzugefügt */}
          {activeWidgets.includes('zuletzt') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Zuletzt hinzugefügt</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentProjects.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '13px' }}>Keine Liegenschaften</p>
                ) : recentProjects.map(p => {
                  const status = getProjectStatus(p);
                  return (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                      onClick={() => window.location.href = `/projects/${p.id}`}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: STATUS_COLOR[status], flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0', fontSize: '12px', color: '#333', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                        <p style={{ margin: '0', fontSize: '10px', color: '#aaa' }}>{p.city}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={s.controls}>
          <div style={s.searchWrapper}>
            <input
              style={s.searchInput}
              placeholder="Liegenschaft, Adresse, Eigentümer suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={s.viewToggle}>
            <button style={{ ...s.toggleBtn, ...(view === 'map' ? s.toggleBtnActive : {}) }} onClick={() => setView('map')}>Karte</button>
            <button style={{ ...s.toggleBtn, ...(view === 'list' ? s.toggleBtnActive : {}) }} onClick={() => setView('list')}>Liste</button>
          </div>
        </div>

        {/* Map View - immer im DOM, nur versteckt */}
          <div style={{...s.mapWrapper, display: view === 'map' ? 'flex' : 'none' }}>
            <div style={s.addressSearchWrapper}>
              <input
                style={s.addressSearchInput}
                placeholder="Adresse auf Karte suchen..."
                value={addressSearch}
                onChange={e => setAddressSearch(e.target.value)}
              />
              {addressResults.length > 0 && (
                <div style={s.addressResults}>
                  {addressResults.map((r, i) => (
                    <div key={i} style={s.addressResult} onClick={() => flyToAddress(r)}>
                      {r.display_name.split(',').slice(0, 3).join(', ')}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={s.legend}>
              {Object.entries(STATUS_LABEL).map(([status, label]) => (
                <div key={status} style={s.legendItem}>
                  <div style={{ ...s.legendDot, backgroundColor: STATUS_COLOR[status as keyof typeof STATUS_COLOR] }} />
                  <span style={s.legendLabel}>{label}</span>
                </div>
              ))}
            </div>
            <div ref={mapContainer} style={s.mapContainer} />
          </div>

        {/* List View */}
        {view === 'list' && (
          <div style={s.listWrapper}>
            {filtered.length === 0 ? (
              <div style={s.empty}>
                <p style={{ color: '#aaa', margin: '0' }}>Keine Liegenschaften gefunden</p>
              </div>
            ) : (
              <div style={s.listGrid}>
                {filtered.map(project => {
                  const status = getProjectStatus(project);
                  const color = STATUS_COLOR[status];
                  const totalFiles = project.plots?.reduce((sum, p) =>
                    sum + (p.files?.length || 0) + p.buildings?.reduce((s, b) => s + (b.files?.length || 0), 0), 0) || 0;
                  const totalBldgs = project.plots?.reduce((sum, p) => sum + (p.buildings?.length || 0), 0) || 0;
                  return (
                    <div key={project.id} style={{ ...s.listCard, borderLeft: `4px solid ${color}` }}>
                      <div style={s.listCardHeader}>
                        <div style={{ flex: 1 }}>
                          <h3 style={s.listCardTitle}>{project.name}</h3>
                          {project.usageType && <span style={s.badge}>{project.usageType}{project.usageSubType ? ` · ${project.usageSubType}` : ''}</span>}
                        </div>
                        <div style={{ ...s.statusBadge, backgroundColor: color + '20', color }}>
                          <div style={{ ...s.statusDot, backgroundColor: color }} />
                          {STATUS_LABEL[status]}
                        </div>
                      </div>
                      <p style={s.listCardAddr}>📍 {project.street}, {project.zipCode} {project.city}</p>
                      {project.owner && <p style={s.listCardOwner}>🏛️ {project.owner}</p>}
                      <div style={s.listCardStats}>
                        <span style={s.listStat}>🏢 {totalBldgs} Gebäude</span>
                        <span style={s.listStat}>📁 {totalFiles} Dateien</span>
                        <span style={s.listStat}>🏞️ {project.plots?.length || 0} Grundstücke</span>
                      </div>
                      <button style={s.listCardBtn} onClick={() => window.location.href = `/projects/${project.id}`}>
                        Öffnen →
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  welcome: { fontSize: '22px', color: '#0f2744', margin: '0' },
  subtitle: { color: '#888', margin: '4px 0 0 0', fontSize: '13px' },
  configBtn: { backgroundColor: 'white', border: '1px solid #ddd', color: '#666', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  widgetConfigPanel: { backgroundColor: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexShrink: 0 },
  widgetRow: { display: 'flex', gap: '12px', overflowX: 'auto', flexShrink: 0, paddingBottom: '4px' },
  widget: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: '220px', maxWidth: '280px', flexShrink: 0 },
  widgetTitle: { margin: '0 0 12px 0', fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  controls: { display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 },
  searchWrapper: { flex: 1 },
  searchInput: { width: '100%', padding: '9px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  viewToggle: { display: 'flex', gap: '4px', backgroundColor: 'white', borderRadius: '8px', padding: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  toggleBtn: { padding: '6px 14px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '13px', color: '#888' },
  toggleBtnActive: { backgroundColor: '#00c49a', color: 'white' },
  mapWrapper: { flex: 1, position: 'relative', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minHeight: 0 },
  addressSearchWrapper: { position: 'absolute', top: '12px', left: '12px', zIndex: 10, width: '280px' },
  addressSearchInput: { width: '100%', padding: '9px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', fontFamily: 'inherit', boxSizing: 'border-box' },
  addressResults: { backgroundColor: 'white', borderRadius: '8px', marginTop: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', overflow: 'hidden' },
  addressResult: { padding: '10px 14px', cursor: 'pointer', fontSize: '12px', color: '#333', borderBottom: '1px solid #f0f0f0' },
  legend: { position: 'absolute', bottom: '24px', left: '12px', zIndex: 10, backgroundColor: 'white', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '6px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  legendDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontSize: '12px', color: '#555' },
  mapContainer: { width: '100%', height: '100%' },
  listWrapper: { flex: 1, overflow: 'auto' },
  empty: { textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '12px' },
  listGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' },
  listCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  listCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' },
  listCardTitle: { margin: '0 0 4px 0', color: '#0f2744', fontSize: '15px' },
  badge: { backgroundColor: '#f0f4ff', color: '#3498db', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  statusBadge: { display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 },
  statusDot: { width: '6px', height: '6px', borderRadius: '50%' },
  listCardAddr: { color: '#666', fontSize: '13px', margin: '0 0 4px 0' },
  listCardOwner: { color: '#888', fontSize: '12px', margin: '0 0 10px 0' },
  listCardStats: { display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' },
  listStat: { color: '#888', fontSize: '12px' },
  listCardBtn: { backgroundColor: 'transparent', border: '1px solid #00c49a', color: '#00c49a', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', width: '100%' },
};

export default Dashboard;
